import { GatsbyImage, getImage } from 'gatsby-plugin-image'
import { useEffect, useRef, useState } from 'react'
import styled, { css } from 'styled-components'
import { useIntl } from '../utils/IntlContext'
import { createAppUrl } from '../utils/locales'
import media from '../utils/media'
import CTA from './CTA'
import MarkdownContent from './MarkdownContent'
import PageSectionResumeTips from './PageSectionResumeTips'
import TableOfContents from './TableOfContents'

const StyledTOCWrapper = styled.div`
  position: relative;
  margin-bottom: 25px;
  margin-top: -40px;
  top: 45px;
  right: 0;
  z-index: 1;

  ${media.md`
    position: absolute;
    margin-top: unset;
    margin-bottom: unset;
    top: 0;
    width: 20vw;
    margin-right: 25px;
  `}
`

const StickyResumeWrapper = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  width: 20vw;
  margin: unset;
  margin-right: 25px;
  display: none;

  ${media.md`
    display: block;
  `}
`

const StickyResume = styled(GatsbyImage)`
  max-width: 288px;
  border-radius: var(--border-radius-large);
  box-shadow: var(--box-shadow-primary-small);
  margin-bottom: 16px;
`

const StickyContent = styled.div`
  position: sticky;
  top: calc(32px + var(--nav-header-height));
  max-width: 300px;
  margin-bottom: 48px;
  margin-top: ${({ $stickyMargin }) => $stickyMargin};
  text-align: center;
  float: right;
`

const CopyContent = styled.div`
  position: relative;
  margin: auto;
  padding: var(--spacing-md);
  display: flex;
  flex-wrap: wrap;
  flex-direction: column-reverse;
  max-width: var(--max-width);

  ${media.md`
    display: block;
  `}
`

const ResumeTipsWrapper = styled.div`
  border-bottom: 1px solid var(--color-primary);

  ${media.md`
    border-bottom: unset;
  `}
`

const additionalStyles = css`
  && {
    max-width: calc(800px + 20vw);
    margin: auto;
    padding: 50px 0 25px;
    width: 100%;

    ${media.md`
      padding: 0 20vw 50px 0;
      border-bottom: unset;
    `}
  }

  h2[id]:before,
  h3[id]:before {
    display: block;
    visibility: hidden;
    content: '';
    width: 0;
  }

  h2:first-child {
    margin-top: 0;
  }

  ul:not(.positiveBlock ul, .negativeBlock ul, .standardList) {
    padding: 24px 24px 24px 42px;
    border-radius: var(--border-radius-large);
    background-color: var(--color-background-light-2);
    margin-left: unset;

    li {
      &::marker {
        color: var(--color-primary);
      }
    }
  }
`

const SampleExampleCopyContent = ({
  howTo,
  resume_tips,
  signupLink,
  tableOfContents,
  isResumeSample,
  highlightedResume,
  displayTOC,
  displayStickyResume,
}) => {
  const [stickyMargin, setStickyMargin] = useState('500px')
  const TOCHeightRef = useRef(null)
  const { locale, t } = useIntl()

  const resetMargin = () => {
    const marginTop = `${TOCHeightRef.current?.clientHeight + 100}px`
    setStickyMargin(marginTop)
  }

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    resetMargin()
    window.addEventListener('resize', resetMargin)

    const removeResizeListener = () => {
      window.removeEventListener('resize', resetMargin)
    }

    return removeResizeListener
  }, [])

  return (
    <CopyContent>
      {howTo ? (
        <MarkdownContent
          el={howTo.childMarkdownRemark.htmlAst}
          images={howTo.childMarkdownRemark.images}
          additionalStyles={additionalStyles}
        />
      ) : resume_tips ? (
        <ResumeTipsWrapper>
          <PageSectionResumeTips section={resume_tips} />
        </ResumeTipsWrapper>
      ) : null}
      {displayTOC && tableOfContents && (
        <StyledTOCWrapper ref={TOCHeightRef}>
          <TableOfContents
            tableOfContents={tableOfContents}
            title={t('table_of_contents')}
          />
        </StyledTOCWrapper>
      )}
      {displayStickyResume && (
        <StickyResumeWrapper>
          <StickyContent $stickyMargin={stickyMargin}>
            <StickyResume
              image={getImage(highlightedResume.image)}
              alt={
                isResumeSample
                  ? `${highlightedResume.title} Resume Sample and Template`
                  : t('example_image_alt', { name: highlightedResume.title })
              }
              title={
                isResumeSample
                  ? `Best ${highlightedResume.title} Resume Sample and Template for ${new Date().getFullYear()}`
                  : t('example_image_title', { name: highlightedResume.title, year: new Date().getFullYear() })
              }
            />
            {highlightedResume?.sample && isResumeSample ? (
              <CTA
                $primary
                to={createAppUrl(`/?sample=${highlightedResume.slug}`, locale)}
                rel="nofollow">
                {t('use_this_sample')}
              </CTA>
            ) : (
              <CTA
                $primary
                to={signupLink}>
                {t('signup')}
              </CTA>
            )}
          </StickyContent>
        </StickyResumeWrapper>
      )}
    </CopyContent>
  )
}

export default SampleExampleCopyContent
